import React from "react";
import { Skeleton } from "./Loader";

/**
 * StatCard — Ocean Blue Design System
 * KPI / summary cards for dashboard and report headers.
 *
 * Props:
 *  title       : string
 *  value       : string | number
 *  prefix      : string   — e.g. "₹" for currency
 *  suffix      : string   — e.g. "%" for percentages
 *  icon        : ReactNode
 *  trend       : { value: number, label?: string, direction: "up"|"down"|"neutral" }
 *  accent      : "primary"|"success"|"warning"|"danger"|"info"
 *  description : string   — secondary helper text below value
 *  isLoading   : boolean
 *  footer      : ReactNode
 *  onClick     : () => void
 */

const ACCENTS = {
  primary: {
    bar: "bg-[#004BB5]",
    icon: "bg-[#E8F0FE] text-[#004BB5]",
    value: "text-[#0F172A]",
  },
  success: {
    bar: "bg-[#10B981]",
    icon: "bg-[#ECFDF5] text-[#059669]",
    value: "text-[#0F172A]",
  },
  warning: {
    bar: "bg-[#F59E0B]",
    icon: "bg-[#FFFBEB] text-[#D97706]",
    value: "text-[#0F172A]",
  },
  danger: {
    bar: "bg-[#EF4444]",
    icon: "bg-[#FEF2F2] text-[#DC2626]",
    value: "text-[#0F172A]",
  },
  info: {
    bar: "bg-[#3B82F6]",
    icon: "bg-[#EFF6FF] text-[#2563EB]",
    value: "text-[#0F172A]",
  },
};

const TrendArrowUp = () => (
  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
  </svg>
);
const TrendArrowDown = () => (
  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const StatCard = ({
  title,
  value,
  prefix,
  suffix,
  icon,
  trend,
  accent = "primary",
  description,
  isLoading = false,
  footer,
  onClick,
  accentBar = false,
  className = "",
  customBg = "",
  customIconBg = "",
}) => {
  const colors = ACCENTS[accent] ?? ACCENTS.primary;
  const isClickable = typeof onClick === "function";

  const trendColors = {
    up: "text-[#059669] bg-[#ECFDF5]",
    down: "text-[#DC2626] bg-[#FEF2F2]",
    neutral: "text-[#475569] bg-[#F1F5F9]",
  };

  if (isLoading) return <Skeleton.KpiCard className={className} />;

  return (
    <div
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === "Enter" && onClick() : undefined}
      className={[
        /* Base card */
        "relative rounded-2xl overflow-hidden",
        customBg || "bg-white border border-[#E2E8F0]",
        "shadow-[0_1px_4px_rgba(0,0,0,0.03),0_1px_2px_rgba(0,0,0,0.02)]",
        "transition-all duration-200 ease-out",
        /* Interactive state */
        isClickable
          ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)]"
          : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Left accent bar */}
      {accentBar && (
        <div className={["absolute top-0 left-0 w-1.5 h-full rounded-l-2xl", colors.bar].join(" ")} />
      )}

      {/* Card content */}
      <div className="p-4 sm:p-5 flex flex-col justify-between min-h-[96px]">
        {/* Row 1: title + icon */}
        <div className="flex items-center justify-between gap-2.5 mb-2.5">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider leading-tight select-none">
            {title}
          </p>
          {icon && (
            <span
              className={
                customIconBg || [
                  "inline-flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0",
                  colors.icon,
                ].join(" ")
              }
            >
              {icon}
            </span>
          )}
        </div>

        {/* Row 2: value */}
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <p
            className={[
              "font-display font-black tracking-tight leading-none text-2xl text-slate-900",
              colors.value,
            ].join(" ")}
          >
            {prefix && (
              <span className="font-bold text-slate-800 mr-0.5">{prefix}</span>
            )}
            {value ?? "—"}
            {suffix && (
              <span className="font-bold text-slate-800 ml-0.5">{suffix}</span>
            )}
          </p>

          {/* Trend badge */}
          {trend && (
            <span
              className={[
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold mb-0.5",
                trendColors[trend.direction] ?? trendColors.neutral,
              ].join(" ")}
            >
              {trend.direction === "up" && <TrendArrowUp />}
              {trend.direction === "down" && <TrendArrowDown />}
              {trend.value !== undefined && `${Math.abs(trend.value)}%`}
            </span>
          )}
        </div>

        {/* Row 3: description / trend label */}
        {(description || trend?.label) && (
          <p className="text-xs text-[#94A3B8] leading-snug mt-1">
            {description}
            {trend?.label && (
              <span className="text-[#94A3B8]"> · {trend.label}</span>
            )}
          </p>
        )}
      </div>

      {/* Optional footer */}
      {footer && (
        <div className="px-5 py-3 border-t border-[#F1F5F9] bg-[#F8FAFC]">{footer}</div>
      )}
    </div>
  );
};

/**
 * StatCardGrid — responsive 4-column grid for KPI cards
 */
export const StatCardGrid = ({ children, cols = 4, className = "" }) => {
  const colsMap = {
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
  };
  return (
    <div className={["grid gap-3", colsMap[cols] ?? colsMap[4], className].join(" ")}>
      {children}
    </div>
  );
};

export default StatCard;
